import type { GatewayModelInfo } from '@/app/sdk';

/** Editor-authored descriptions when the Cursor catalogue omits them. */
const DESCRIPTION_BY_ID: Record<string, string> = {
  'composer-2.5': 'Cursor\'s fast default model for everyday coding and edits in the editor.',
  'claude-opus-4-8-high-thinking': 'Anthropic Opus with high reasoning — best for difficult architecture and multi-step refactors.',
  'gpt-5.5-medium': 'OpenAI\'s flagship model with balanced medium reasoning for complex tasks.',
  'claude-4.6-sonnet-medium-thinking': 'Sonnet with medium reasoning — strong balance of speed and quality for most agent work.',
  'gpt-5.3-codex-medium': 'OpenAI Codex tuned for code generation and repository-aware changes.',
  'claude-4.6-opus-high-thinking': 'Opus with high reasoning for demanding planning and implementation.',
  'kimi-k2.5': 'Moonshot Kimi K2.5 for long-context general assistance.',
};

const DESCRIPTION_BY_PATTERN: { test: (model: GatewayModelInfo) => boolean; text: string }[] = [
  {
    test: model => model.id.includes('composer'),
    text: 'Cursor Composer model optimised for interactive coding in the Genesys editor.',
  },
  {
    test: model => model.id.includes('opus'),
    text: 'Anthropic Opus — highest capability Claude family model for hard problems.',
  },
  {
    test: model => model.id.includes('sonnet'),
    text: 'Anthropic Sonnet — fast, capable model for everyday agent and plan workflows.',
  },
  {
    test: model => model.id.includes('codex') || model.id.includes('gpt'),
    text: 'OpenAI model suited to code understanding, generation, and tool use.',
  },
  {
    test: model => model.id.includes('kimi'),
    text: 'Moonshot Kimi model with strong long-context performance.',
  },
  {
    test: model => model.displayName.toLowerCase().includes('auto'),
    text: 'Automatically picks an efficient model for the task.',
  },
  {
    test: model => model.displayName.toLowerCase().includes('premium'),
    text: 'Premium intelligence tier for higher-quality responses.',
  },
];

export function getAssistantModelDescription(model: GatewayModelInfo): string {
  const catalogue = model.description?.trim();
  if (catalogue) {
    return catalogue;
  }

  const byId = DESCRIPTION_BY_ID[model.id];
  if (byId) {
    return byId;
  }

  for (const entry of DESCRIPTION_BY_PATTERN) {
    if (entry.test(model)) {
      return entry.text;
    }
  }

  return `${model.displayName}`;
}
